// =============================================
// audioStore — media blob store for music files (server-first)
// =============================================
// Audio/video/cover blobs are persisted to the SERVER blob store the moment an
// upload finishes (so a cleared browser never loses a song — see CLAUDE.md →
// File Storage Rules). The `mus_tracks_all` row stores the key in `storage_path`
// (cover key in `id3_raw.cover_key`):
//   • blob://… or https://… → SERVER blob volume (/mnt/blobs, via /api/blob)
//   • idb://<key>            → local IndexedDB (offline / logged-out fallback)
// IndexedDB is only a cache/fallback; `storeAudioBlob` writes to the server first,
// `getAudioUrl` reads server-first, and idb blobs migrate up on the next chance.
// =============================================
import {
  isServerBlobKey,
  putServerBlob as putServerBlobBinary,
  getServerBlobUrl,
  deleteServerBlob,
} from './serverBlobStore';

const DB_NAME = 'akt_media';
const STORE = 'audio';
const DB_VERSION = 1;

const IDB_PREFIX = 'idb://';

let _dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

/** Build a fresh storage key. Caller stores `storageKey` in the DB row. */
export function newAudioKey(): string {
  // Time + random, base36 — readable and collision-safe enough for one device.
  const rand = Math.random().toString(36).slice(2, 10);
  return IDB_PREFIX + Date.now().toString(36) + '-' + rand;
}

function bareKey(key: string): string {
  return key.startsWith(IDB_PREFIX) ? key.slice(IDB_PREFIX.length) : key;
}

/** Is this a key we own (IndexedDB), vs a real remote URL? */
export function isIdbKey(key: string | null | undefined): boolean {
  return !!key && key.startsWith(IDB_PREFIX);
}

/** Store a media blob under `key` in IndexedDB (fallback store). */
export async function putAudio(key: string, blob: Blob): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(blob, bareKey(key));
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

function extFor(blob: Blob, filename?: string): string {
  const fromName = filename && filename.includes('.') ? filename.split('.').pop()! : '';
  if (fromName) return fromName.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8);
  const t = (blob.type || '').toLowerCase();
  if (t.includes('mpeg') || t.includes('mp3')) return 'mp3';
  if (t.includes('mp4') || t.includes('m4a')) return 'm4a';
  if (t.includes('wav')) return 'wav';
  if (t.includes('ogg')) return 'ogg';
  if (t.includes('flac')) return 'flac';
  if (t.includes('webm')) return 'webm';
  if (t.includes('png')) return 'png';
  if (t.includes('jpeg') || t.includes('jpg')) return 'jpg';
  return 'bin';
}

/**
 * Persist a media blob and return its storage key. Server-first: uploads to the
 * blob volume (`blob:music/<acct>/…`) so the durable copy lands on the server
 * immediately; falls back to a local IndexedDB key (`idb://…`) only when offline
 * / logged out. `accountOwnerId` scopes the server blob; `filename` hints the ext.
 */
export async function storeAudioBlob(
  blob: Blob,
  accountOwnerId?: number | string | null,
  filename?: string,
): Promise<string> {
  const serverKey = await putServerBlobBinary(blob, {
    scope: 'music',
    accountOwnerId,
    ext: extFor(blob, filename),
  });
  if (serverKey) return serverKey;
  // offline / logged out → local fallback; migrated up later by migrateMusicBlobsToServer.
  const key = newAudioKey();
  await putAudio(key, blob);
  return key;
}

/** Fetch the raw blob for an IndexedDB `key`, or null if missing. */
export async function getAudioBlob(key: string): Promise<Blob | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(bareKey(key));
    req.onsuccess = () => resolve((req.result as Blob) || null);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Get a playable object URL for `key`. Reads server-first (blob:/https keys),
 * then IndexedDB. The caller revokes it (URL.revokeObjectURL) when done — the
 * player does this when the track changes. Null if the blob is missing.
 */
export async function getAudioUrl(key: string): Promise<string | null> {
  if (isServerBlobKey(key)) {
    const url = await getServerBlobUrl(key);
    if (url) return url;
    // server unreachable / not replicated — fall through to a local copy if any
  }
  const blob = await getAudioBlob(key);
  return blob ? URL.createObjectURL(blob) : null;
}

/** Remove a media blob (called on track delete). Server-aware, best-effort. */
export async function deleteAudio(key: string): Promise<void> {
  if (isServerBlobKey(key)) {
    await deleteServerBlob(key);
    return;
  }
  if (!isIdbKey(key)) return;
  const db = await openDb();
  return new Promise((resolve) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(bareKey(key));
    tx.oncomplete = () => resolve();
    tx.onerror = () => resolve(); // best-effort — never block a delete on this
  });
}
